/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "nesting" }] */
import { FormEvent, useEffect, useState } from "react";
import {
  AffiliateCommandReceipt,
  AffiliateLifecyclePreview,
  AffiliateOperation,
  AffiliatePayoutPreview,
  AffiliateState,
} from "../../interfaces/affiliate";
import { affiliateError } from "../../hooks/api/admin/useAffiliates";

type Operation = AffiliateLifecyclePreview["operation"];
export function LifecycleReviewPanel({
  data,
  disabled,
  command,
}: {
  data: AffiliateState;
  disabled: boolean;
  command: (
    operation: AffiliateOperation,
    payload: Record<string, unknown>
  ) => Promise<
    AffiliateCommandReceipt | AffiliatePayoutPreview | AffiliateLifecyclePreview
  >;
}) {
  const [affiliate, setAffiliate] = useState("");
  const [operation, setOperation] = useState<Operation>("set_payout_hold");
  const [held, setHeld] = useState(true);
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [preview, setPreview] = useState<AffiliateLifecyclePreview>();
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const fingerprint = JSON.stringify([affiliate, operation, held, reason]);
  useEffect(() => {
    setPreview(undefined);
    setConfirmation("");
    setMessage("");
  }, [fingerprint]);
  const supported =
    data.lifecycle_policy === "launch_v1" &&
    data.capabilities?.[operation] === true;
  const ready =
    supported &&
    !disabled &&
    data.affiliates.some((item) => item.address === affiliate) &&
    Boolean(reason.trim());
  const payload = {
    operation,
    affiliate,
    reason: reason.trim(),
    ...(operation === "set_payout_hold" ? { held } : {}),
  };
  const matches = (value: AffiliateLifecyclePreview) =>
    value.policy === "launch_v1" &&
    value.operation === operation &&
    value.affiliate === affiliate &&
    value.reason === reason.trim() &&
    (operation !== "set_payout_hold" || value.held === held) &&
    /^[a-f0-9]{64}$/.test(value.digest) &&
    Number.isFinite(Date.parse(value.evaluated_at)) &&
    value.effects &&
    typeof value.effects === "object";
  const loadPreview = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setPreview(undefined);
    setError("");
    setMessage("");
    try {
      const response = await command("preview_lifecycle", payload);
      if (!("digest" in response) || !matches(response))
        throw new Error(
          "Preview does not match the requested action. Refresh and try again."
        );
      setPreview(response);
    } catch (failure) {
      setError(affiliateError(failure));
    }
  };
  const execute = async () => {
    if (
      !ready ||
      !preview ||
      !matches(preview) ||
      confirmation.toLowerCase() !== affiliate.toLowerCase()
    )
      return;
    setError("");
    try {
      const execution = { affiliate, reason: reason.trim(), ...(operation === "set_payout_hold" ? { held } : {}) };
      await command(operation, {
        ...execution,
        expected_digest: preview.digest,
      });
      setPreview(undefined);
      setConfirmation("");
      setMessage(
        "Command accepted. Refreshed backend state and payment history confirm its outcome."
      );
    } catch (failure) {
      setError(affiliateError(failure));
    }
  };
  if (data.lifecycle_policy !== "launch_v1") return null;
  return (
    <section aria-label="Affiliate payout holds and recovery">
      <h2>Payout holds, clawbacks and termination</h2>
      <p>
        Review the backend’s exact financial effects before confirming. A
        changed balance invalidates the preview; obtain a new preview when
        requested.
      </p>
      <form onSubmit={loadPreview}>
        <fieldset disabled={disabled}>
          <label>
            Affiliate to review
            <select
              required
              value={affiliate}
              onChange={(event) => setAffiliate(event.target.value)}
            >
              <option value="">Choose affiliate</option>
              {data.affiliates.map((item) => (
                <option key={item.address} value={item.address}>
                  {item.address}
                </option>
              ))}
            </select>
          </label>
          <label>
            Lifecycle action
            <select
              value={operation}
              onChange={(event) =>
                setOperation(event.target.value as Operation)
              }
            >
              <option value="set_payout_hold">
                Set or release payout hold
              </option>
              <option value="clawback">Claw back eligible rewards</option>
              <option value="terminate">Terminate affiliate</option>
            </select>
          </label>
          {operation === "set_payout_hold" && (
            <label>
              Hold action
              <select
                value={held ? "hold" : "release"}
                onChange={(event) => setHeld(event.target.value === "hold")}
              >
                <option value="hold">Hold payouts</option>
                <option value="release">Release payout hold</option>
              </select>
            </label>
          )}
          <label>
            Reason for audit record
            <input
              required
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <button type="submit" disabled={!ready}>
            Preview lifecycle effects
          </button>
        </fieldset>
      </form>
      {!supported && <p>This action is not enabled by the backend.</p>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {preview && matches(preview) && (
        <div>
          <h3>Review {operation.replace(/_/g, " ")}</h3>
          <p>Point amounts are separate from USD amounts. Retained earned-point remainders stay in frozen funding; they are not cash refunds.</p>
          <p>
            Affiliate: <code>{preview.affiliate}</code> · Evaluated{" "}
            {preview.evaluated_at}
          </p>
          {operation === "terminate" && (
            <p>
              {preview.affected_wallets?.length || 0} linked wallets return to
              standard fees after {preview.fee_notice_days} days. Existing
              attribution{" "}
              {preview.retains_attribution ? "is retained" : "is not retained"}.
            </p>
          )}
          <table>
            <thead>
              <tr>
                <th>Effect</th>
                <th>Backend amount / status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(preview.effects)
                .filter(([, value]) => !Array.isArray(value))
                .map(([key, value]) => (
                  <tr key={key}>
                    <th>{key.replace(/_/g, " ")}</th>
                    <td>{String(value)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
          <details>
            <summary>Inspect affected balances</summary>
            <pre>{JSON.stringify(preview.effects, null, 2)}</pre>
          </details>
          <label>
            Type affiliate address to confirm
            <input
              value={confirmation}
              autoComplete="off"
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={
              !ready || confirmation.toLowerCase() !== affiliate.toLowerCase()
            }
            onClick={execute}
          >
            Confirm reviewed lifecycle action
          </button>
        </div>
      )}
    </section>
  );
}
